import React, { useEffect, lazy, useState, Suspense } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

import Header from "../components/Header";
import Footer from "../components/footer";
import WhatsAppButton from "../components/WhatsAppButton";
import SeoHead from "../components/SeoHead";
import homeShareImage from "../assets/img/QBits9.webp";
import siteData from "../data/siteData";
import {
  createBreadcrumbList,
  createFaqPageSchema,
  createItemListSchema,
  createLocalBusinessSchema,
  createOrganizationSchema,
  createProfessionalServiceSchema,
  createWebPageSchema,
  createWebSiteSchema,
} from "../utils/seo";

const QbitsMainHero = lazy(() => import("../components/QbitsMainHero"));
const QbitsMainLogos = lazy(() => import("../components/QbitsMainLogos"));
const QbitsMainServices = lazy(() => import("../components/QbitsMainServices"));
const QbitsMainOurClient = lazy(() => import("../components/QbitsMainOurClient"));
const QbitsMainLetsTalk = lazy(() => import("../components/QbitsMainLetsTalk"));
const QbitsMainProjectSlider = lazy(() => import("../components/QbitsMainProjectSlider"));
const QbitsMainDifferent = lazy(() => import("../components/QbitsMainDifferent"));
const QbitsMainAbout = lazy(() => import("../components/QbitsMainAbout"));
const QbitsMainAutomationExamples = lazy(() => import("../components/QbitsMainAutomationExamples"));
const QbitsMainTechnology = lazy(() => import("../components/QbitsMainTechnology"));
const QbitsMainCompany = lazy(() => import("../components/QbitsMainCompany"));
const QbitsMainFaq = lazy(() => import("../components/QbitsMainFaq"));

// Set to true to restore the Technology Stack section.
const SHOW_TECHNOLOGY_STACK = false;

// GEO: Homepage FAQs exported for FAQPage schema
const homepageFaqs = [
  {
    question: "What is AI automation for small business?",
    answer:
      "AI automation for small business means using smart software to handle repetitive work like follow-ups, customer replies, reporting, reminders, and internal admin tasks.",
  },
  {
    question: "How can AI automation help my small business?",
    answer:
      "It helps you save time, reduce manual errors, improve response speed, and handle more work without adding the same amount of admin effort.",
  },
  {
    question: "What tasks can be automated first?",
    answer:
      "The best first tasks are usually lead follow-up, support replies, reporting, invoice reminders, appointment updates, and internal approvals.",
  },
  {
    question: "Is AI automation suitable for small businesses?",
    answer:
      "Yes. Small businesses often benefit the most because automation helps lean teams save time, reduce busywork, and keep operations moving without extra overload.",
  },
  {
    question: "Is AI automation too complex for a small business?",
    answer:
      "No. The best approach is to start with one simple workflow that solves a real business problem, then expand only after the first result is clear.",
  },
  {
    question: "How much does small business automation usually cost?",
    answer:
      "Cost depends on the workflow, tools, and complexity. Most small businesses should start with one focused automation so the investment stays practical and measurable.",
  },
  {
    question: "How long does it take to get started?",
    answer:
      "A focused automation project can often be planned and launched within a few weeks, depending on the workflow and integrations involved.",
  },
  {
    question: "Do I need technical knowledge to use automation?",
    answer:
      "No. QBITS9 handles the setup and builds the workflow around tools your team already uses, so the process stays simple for business owners and staff.",
  },
  {
    question: "Can you connect automation with my existing software?",
    answer:
      "Yes. We can connect automation with tools like CRMs, inboxes, spreadsheets, helpdesks, dashboards, and other business systems you already rely on.",
  },
  {
    question: "What is the best workflow to automate first?",
    answer:
      "Usually it is the task that happens most often, wastes the most time, and is easiest to measure, such as lead routing, support handling, or reporting.",
  },
];

const Qbits9Main = () => {
  const location = useLocation();

  const [showBelowFold, setShowBelowFold] = useState(false);
  const [heroReady, setHeroReady] = useState(false);

  const jsonLd = [
    createOrganizationSchema({ logoUrl: homeShareImage, sameAs: siteData.sameAs }),
    createLocalBusinessSchema({ logoUrl: homeShareImage }),
    createProfessionalServiceSchema({ logoUrl: homeShareImage }),
    createWebSiteSchema({
      searchUrl: `${siteData.siteUrl}/blog?q={search_term_string}`,
    }),
    createWebPageSchema({
      name: "AI Automation for Small Business | QBits9",
      description:
        "QBits9 provides AI automation for small business owners who want to save time, reduce manual work, and grow with simpler day-to-day operations.",
      path: "/",
      type: "WebPage",
      imageUrl: homeShareImage,
      datePublished: "2022-01-01",
      dateModified: "2026-09-29",
      keywords: siteData.serviceKeywords.aiAutomation,
      speakableSelectors: [
        ".home-hero-description",
        ".home-location-copy",
        ".home-benefit-copy",
      ],
      breadcrumbs: [{ name: "Home", path: "/" }],
    }),
    // GEO: FAQPage schema for homepage FAQs — critical for AI answer extraction
    createFaqPageSchema({
      faqs: homepageFaqs,
      path: "/",
      name: "AI Automation FAQ",
      description: "Frequently asked questions about AI automation for small businesses",
    }),
    // GEO: ItemList for AI service discovery
    createItemListSchema({
      name: "QBits9 Services",
      description: "AI automation and custom software services by QBits9",
      items: [
        {
          name: "AI Automation Services",
          path: "/services/ai-automation",
          description: "Intelligent workflow automation, AI chatbots, automated reporting, and process optimization",
        },
        {
          name: "Web & Mobile App Development",
          path: "/services/web-mobile-development",
          description: "Custom web applications, progressive web apps, and native mobile apps",
        },
        {
          name: "SaaS & ERP Development",
          path: "/services/saas-erp-development",
          description: "Custom SaaS platforms, enterprise resource planning systems, and CRM integrations",
        },
        {
          name: "Desktop Software Development",
          path: "/services/desktop-software",
          description: "Cross-platform desktop applications and internal business tools",
        },
      ],
    }),
  ];

  useEffect(() => {
    const lenis = new Lenis({
      smoothWheel: true,
      smoothTouch: false,
      lerp: 0.05,
    });

    window.lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      window.lenis = undefined;
    };
  }, []);

  useEffect(() => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => setShowBelowFold(true), { timeout: 2000 });
    } else {
      setTimeout(() => setShowBelowFold(true), 150);
    }
  }, []);

  useEffect(() => {
    if (!location.hash) return;

    const HEADER_OFFSET = 90;
    const id = location.hash.replace("#", "");
    let tries = 0;

    const scrollToSection = () => {
      const el = document.getElementById(id);

      if (!el && tries < 40) {
        tries++;
        setTimeout(scrollToSection, 100);
        return;
      }

      if (!el) return;

      const y =
        el.getBoundingClientRect().top +
        window.pageYOffset -
        HEADER_OFFSET;

      if (window.lenis) {
        window.lenis.scrollTo(y);
      } else {
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };

    scrollToSection();
  }, [location.hash, showBelowFold]);

  return (
    <>
      <SeoHead
        title="AI Automation & Custom Software for Business | QBits9"
        description="QBits9 builds AI automations, custom web and mobile apps, desktop software, and ERP integrations that reduce manual work and improve business operations."
        canonicalPath="/"
        image={homeShareImage}
        imageAlt="QBits9 AI automation and digital solutions"
        keywords={siteData.serviceKeywords.aiAutomation}
        extraMeta={[
          { name: "geo.region", content: siteData.geoRegion },
          { name: "geo.placename", content: siteData.geoPlacename },
          { name: "geo.position", content: siteData.geoPosition },
          { name: "ICBM", content: siteData.geoICBM },
        ]}
        jsonLd={jsonLd}
      />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 bg-white px-4 py-2 rounded shadow">
        Skip to main content
      </a>
      <Header />

      <main id="main-content">
      <Suspense fallback={null}>
        <QbitsMainHero onReady={() => setHeroReady(true)} />
        <QbitsMainLogos />
        <QbitsMainServices />
        <QbitsMainOurClient />

        {showBelowFold && (
          <>
            <QbitsMainProjectSlider />
            <QbitsMainDifferent />
            <QbitsMainAbout />
            <QbitsMainAutomationExamples />
            {SHOW_TECHNOLOGY_STACK && <QbitsMainTechnology />}
            <QbitsMainLetsTalk />
            <QbitsMainCompany />
            <QbitsMainFaq />
          </>
        )}
      </Suspense>

      {heroReady && <Footer />}
      <WhatsAppButton />
      </main>
    </>
  );
};

export default Qbits9Main;
